<template>
    <v-card class="personal-investments elevation-2">
        <v-card-title class="primary--text pb-2">
            <div class="d-flex align-center w-100">
                <v-icon left color="primary" size="28">mdi-wallet</v-icon>
                <div class="flex-grow-1">
                    <div class="text-h5 font-weight-bold">Moje investície</div>
                    <div class="text-caption grey--text">Prehľad všetkých vašich investičných kontraktov</div>
                </div>
                <div class="d-flex align-center">
                    <v-chip v-if="investments.length > 0" color="primary" outlined class="mr-3">
                        <v-icon left small>mdi-file-document</v-icon>
                        {{ investments.length }} {{ contractText }}
                    </v-chip>
                    <v-menu offset-y>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon v-bind="attrs" v-on="on">
                                <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                        </template>
                        <v-list>
                            <v-list-item @click="exportData">
                                <v-list-item-icon>
                                    <v-icon small>mdi-download</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Exportovať údaje</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                            <v-list-item @click="refreshData">
                                <v-list-item-icon>
                                    <v-icon small>mdi-refresh</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>Obnoviť údaje</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
            </div>
        </v-card-title>

        <!-- Empty state -->
        <div v-if="investments.length === 0 && !loading" class="empty-state">
            <v-row justify="center">
                <v-col cols="12" md="8" lg="6">
                    <div class="text-center py-12">
                        <div class="empty-icon mb-6">
                            <v-icon size="120" color="grey lighten-2">mdi-briefcase-outline</v-icon>
                        </div>
                        <div class="text-h5 grey--text text--darken-1 mb-3">Zatiaľ nemáš uzatvorenú žiadnu platnú zmluvu
                            o
                            investícií</div>
                        <div class="text-body-1 grey--text mb-6">
                            Začni investovať a staň sa súčasťou nášho úspechu.
                        </div>
                        <v-btn large color="primary" @click="$emit('start-investing')" class="mb-3">
                            <v-icon left>mdi-rocket-launch</v-icon>
                            Začať investovať
                        </v-btn>
                        <div class="text-caption grey--text">
                            Minimálna investícia už od 10€
                        </div>
                    </div>
                </v-col>
            </v-row>
        </div>

        <!-- Main content with investments -->
        <v-card-text v-else class="pb-2">
            <!-- Enhanced dashboard -->
            <v-row class="mb-6">
                <v-col cols="12">
                    <v-card outlined class="dashboard-card">
                        <v-card-text class="pa-6">
                            <div class="d-flex align-center justify-space-between mb-4">
                                <div class="d-flex align-center">
                                    <v-icon left color="primary" size="24">mdi-chart-donut</v-icon>
                                    <span class="text-h6 font-weight-medium primary--text">Investičný dashboard</span>
                                </div>
                                <v-btn small outlined color="primary" @click="toggleDashboardDetails">
                                    {{ showDashboardDetails ? 'Menej' : 'Viac' }} detailov
                                    <v-icon right small>{{ showDashboardDetails ? 'mdi-chevron-up' : 'mdi-chevron-down'
                                    }}</v-icon>
                                </v-btn>
                            </div>

                            <!-- Key metrics -->
                            <v-row>
                                <v-col v-for="metric in keyMetrics" :key="metric.label" cols="6" md="3">
                                    <div class="metric-card text-center pa-3">
                                        <div class="text-overline">{{ metric.label }}</div>
                                        <div :class="metric.color + '--text'"
                                            class="text-h4 font-weight-bold metric-value">
                                            {{ metric.value }}
                                        </div>
                                        <div v-if="metric.subtitle" class="text-caption grey--text">
                                            {{ metric.subtitle }}
                                        </div>
                                        <v-progress-linear v-if="metric.progress !== undefined" :value="metric.progress"
                                            :color="metric.color" height="4" rounded class="mt-2"></v-progress-linear>
                                    </div>
                                </v-col>
                            </v-row>

                            <!-- Detailed progress section -->
                            <v-expand-transition>
                                <div v-if="showDashboardDetails" class="mt-6">
                                    <v-divider class="mb-4"></v-divider>
                                    <v-row>
                                        <v-col cols="12" md="6">
                                            <div class="text-subtitle-2 mb-3">
                                                <v-icon left small color="success">mdi-trending-up</v-icon>
                                                Pokrok k vráteniu investície
                                            </div>
                                            <div class="d-flex align-center justify-space-between mb-2">
                                                <span class="text-body-2">{{ investmentRecoveryProgress.toFixed(1) }}%
                                                    dokončené</span>
                                                <span class="text-body-2 font-weight-medium">
                                                    {{ formatCurrency(Math.min(totalMyInvestment, totalAnnualPayout)) }}
                                                    / {{
                                                        formatCurrency(totalMyInvestment) }}
                                                </span>
                                            </div>
                                            <v-progress-linear :value="investmentRecoveryProgress" height="12" rounded
                                                :color="investmentRecoveryProgress >= 100 ? 'success' : 'warning'">
                                                <template v-slot:default="{ value }">
                                                    <small class="white--text font-weight-bold">{{ Math.ceil(value)
                                                    }}%</small>
                                                </template>
                                            </v-progress-linear>
                                        </v-col>

                                        <v-col cols="12" md="6">
                                            <div class="text-subtitle-2 mb-3">
                                                <v-icon left small color="info">mdi-trophy</v-icon>
                                                Pokrok k celkovému benefitu
                                            </div>
                                            <div class="d-flex align-center justify-space-between mb-2">
                                                <span class="text-body-2">{{ benefitProgress.toFixed(1) }}%
                                                    dokončené</span>
                                                <span class="text-body-2 font-weight-medium">
                                                    {{ formatCurrency(Math.min(totalMyPayoutClaim, totalAnnualPayout))
                                                    }} / {{
                                                        formatCurrency(totalMyPayoutClaim) }}
                                                </span>
                                            </div>
                                            <v-progress-linear :value="benefitProgress" height="12" rounded
                                                :color="benefitProgress >= 100 ? 'success' : 'info'">
                                                <template v-slot:default="{ value }">
                                                    <small class="white--text font-weight-bold">{{ Math.ceil(value)
                                                    }}%</small>
                                                </template>
                                            </v-progress-linear>
                                        </v-col>
                                    </v-row>
                                </div>
                            </v-expand-transition>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Modelácia predpokladanej tržby -->
            <v-row class="mb-4">
                <v-col cols="12" md="6">
                    <v-text-field v-model.number="modeledAnnualRevenue" label="Modelácia predpokladanej tržby ročne"
                        type="number" suffix="€" outlined dense hide-details @input="updateModeledRevenue">
                        <template v-slot:prepend-inner>
                            <v-icon color="info">mdi-calculator</v-icon>
                        </template>
                    </v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                    <v-slider v-model="payoutPercentage" label="Koeficient platby z tržieb v %" min="60" max="70"
                        step="1" thumb-label :thumb-size="24" class="mt-2" @input="updateModeledRevenue">
                        <template v-slot:append>
                            <v-text-field v-model="payoutPercentage" class="mt-0 pt-0" hide-details single-line
                                type="number" style="width: 60px" @input="updateModeledRevenue"></v-text-field>
                        </template>
                    </v-slider>
                </v-col>
            </v-row>

            <!-- Investments table -->
            <v-row>
                <v-col cols="12">
                    <v-card outlined>
                        <v-card-subtitle class="primary--text font-weight-bold">
                            Detail mojich investícií
                        </v-card-subtitle>
                        <v-card-text>
                            <v-data-table :headers="investmentHeaders" :items="enrichedInvestments" :loading="loading"
                                class="elevation-0" hide-default-footer :items-per-page="-1" :sort-by="['created_at']"
                                :sort-desc="[true]">
                                <template v-slot:item.contract_number="{ item }">
                                    <div class="d-flex align-center">
                                        <v-chip small color="primary" outlined class="mr-2">
                                            {{ item.contract_number }}
                                        </v-chip>
                                    </div>
                                </template>

                                <template v-slot:item.status="{ item }">
                                    <v-chip small :color="getStatusColor(item.status)" dark class="font-weight-medium">
                                        <v-icon left x-small>{{ getStatusIcon(item.status) }}</v-icon>
                                        {{ getStatusText(item.status) }}
                                    </v-chip>
                                </template>

                                <template v-slot:item.investment_amount="{ item }">
                                    <span class="font-weight-bold text-body-1">
                                        {{ formatCurrency(item.investment_amount) }}
                                    </span>
                                </template>

                                <template v-slot:item.coefficient="{ item }">
                                    <v-chip small :color="getCoefficientColor(item.coefficient)" dark>
                                        {{ item.coefficient }}x
                                    </v-chip>
                                </template>

                                <template v-slot:item.payout_claim="{ item }">
                                    <span class="success--text font-weight-bold">
                                        {{ formatCurrency(item.payout_claim) }}
                                    </span>
                                </template>

                                <template v-slot:item.annual_payout="{ item }">
                                    <span class="info--text font-weight-medium">
                                        {{ formatCurrency(item.annual_payout) }}
                                    </span>
                                </template>

                                <template v-slot:item.recovery_balance="{ item }">
                                    <div :class="item.recovery_balance >= 0 ? 'success--text' : 'error--text'">
                                        {{ item.recovery_balance >= 0 ? '+' : '' }}{{
                                            formatCurrency(item.recovery_balance) }}
                                    </div>
                                    <div class="text-caption grey--text">
                                        {{ item.recovery_balance >= 0 ? 'Investícia vrátená' : 'Chýba do vrátenia' }}
                                    </div>
                                </template>

                                <template v-slot:item.benefit_balance="{ item }">
                                    <div :class="item.benefit_balance >= 0 ? 'success--text' : 'error--text'">
                                        {{ item.benefit_balance >= 0 ? '+' : '' }}{{
                                            formatCurrency(item.benefit_balance) }}
                                    </div>
                                    <div class="text-caption grey--text">
                                        {{ item.benefit_balance >= 0 ? 'Benefit dosiahnutý' : 'Chýba do benefitu' }}
                                    </div>
                                </template>

                                <template v-slot:item.created_at="{ item }">
                                    <div>
                                        <div class="text-body-2">{{ formatDate(item.created_at) }}</div>
                                        <div v-if="item.payment_date" class="text-caption success--text">
                                            Uhradené: {{ formatDate(item.payment_date) }}
                                        </div>
                                    </div>
                                </template>

                                <template v-slot:item.actions="{ item }">
                                    <v-menu offset-y>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small v-bind="attrs" v-on="on">
                                                <v-icon>mdi-dots-vertical</v-icon>
                                            </v-btn>
                                        </template>
                                        <v-list dense>
                                            <!-- <v-list-item @click="showContractDetail(item)">
                                                <v-list-item-icon>
                                                    <v-icon small>mdi-eye</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-content>
                                                    <v-list-item-title>Detail</v-list-item-title>
                                                </v-list-item-content>
                                            </v-list-item> -->
                                            <v-list-item @click="downloadContract(item)">
                                                <v-list-item-icon>
                                                    <v-icon small>mdi-download</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-content>
                                                    <v-list-item-title>Stiahnuť PDF</v-list-item-title>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>
                                </template>
                            </v-data-table>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Summary row -->
            <v-row class="mt-4">
                <v-col cols="12">
                    <v-card outlined color="info lighten-5">
                        <v-card-text>
                            <v-row>
                                <v-col cols="12" md="3">
                                    <div class="text-center">
                                        <div class="text-caption">Celková moja investícia spolu</div>
                                        <div class="text-h5 primary--text font-weight-bold">
                                            {{ formatCurrency(totalMyInvestment) }}
                                        </div>
                                    </div>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <div class="text-center">
                                        <div class="text-caption">Celkový nárok (s benefitom)</div>
                                        <div class="text-h5 success--text font-weight-bold">
                                            {{ formatCurrency(totalMyPayoutClaim) }}
                                        </div>
                                    </div>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <div class="text-center">
                                        <div class="text-caption">Očakávaná ročná výplata</div>
                                        <div class="text-h5 info--text font-weight-bold">
                                            {{ formatCurrency(totalAnnualPayout) }}
                                        </div>
                                    </div>
                                </v-col>
                                <v-col cols="12" md="3">
                                    <div class="text-center">
                                        <div class="text-caption">Môj podiel %</div>
                                        <div class="text-h5 warning--text font-weight-bold">
                                            {{ myParticipationPercentage }}%
                                        </div>
                                    </div>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Quick additional investment -->
            <!-- <v-row class="mt-6">
                <v-col cols="12">
                    <v-card outlined class="quick-invest-card">
                        <v-card-title class="text-h6 success--text pb-2">
                            <v-icon left color="success">mdi-flash</v-icon>
                            Ďalšia prípadná investícia
                        </v-card-title>
                        <v-card-text>
                            <v-row align="center">
                                <v-col cols="12" md="4">
                                    <v-text-field v-model.number="additionalInvestment"
                                        label="Chcem ešte investovať sumu" type="number" step="10" min="10" suffix="€"
                                        outlined dense hide-details="auto" :rules="investmentRules"
                                        @focus="$event.target.select()">
                                        <template v-slot:prepend-inner>
                                            <v-icon color="success">mdi-plus</v-icon>
                                        </template>
                                    </v-text-field>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <div class="text-center">
                                        <div class="text-caption">Očakávaný nárok s koeficientom {{
                                            getProjectedCoefficient
                                        }}x</div>
                                        <div class="text-h5 success--text font-weight-bold">
                                            {{ formatCurrency(additionalPayoutClaim) }}
                                        </div>
                                        <div class="text-caption grey--text">
                                            ROI: {{ additionalROI }}% ročne
                                        </div>
                                    </div>
                                </v-col>
                                <v-col cols="12" md="4">
                                    <v-btn large color="success" block :disabled="!isValidAdditionalInvestment"
                                        :loading="investingAdditional" @click="investAdditional">
                                        <v-icon left>mdi-rocket-launch</v-icon>
                                        Investovať ešte
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row> -->
        </v-card-text>
    </v-card>
</template>

<script>
import { APIService } from "@/http/APIService.js";

const apiService = new APIService();


export default {
    name: "PersonalInvestmentsTable",

    props: {
        investments: {
            type: Array,
            default: () => []
        },
        annualRevenue: {
            type: Number,
            default: 3420000
        },
        loading: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            showDashboardDetails: false,
            modeledAnnualRevenue: this.annualRevenue,
            payoutPercentage: 60,
            additionalInvestment: 1000,
            investingAdditional: false,

            investmentHeaders: [
                { text: 'Dátum uzavretia', value: 'created_at', width: '120px' },
                { text: 'Číslo zmluvy', value: 'contract_number', width: '130px' },
                { text: 'Status', value: 'status', width: '100px' },
                { text: 'Koef.', value: 'coefficient', width: '80px', align: 'center' },
                { text: 'Investícia', value: 'investment_amount', align: 'right' },
                { text: 'Nárok', value: 'payout_claim', align: 'right' },
                { text: 'Ročná výplata', value: 'annual_payout', align: 'right' },
                { text: 'Stav investície', value: 'recovery_balance', align: 'right' },
                { text: 'Stav benefitu', value: 'benefit_balance', align: 'right' },
                { text: 'Akcie', value: 'actions', sortable: false, width: '80px' }
            ]
        };
    },

    computed: {
        contractText() {
            if (this.investments.length === 1) return 'zmluva';
            if (this.investments.length < 5) return 'zmluvy';
            return 'zmlúv';
        },

        // Obohať investície o vypočítané hodnoty
        enrichedInvestments() {
            return this.investments.map(investment => {
                const annualPayout = this.calculateAnnualPayout(investment);
                const recoveryBalance = annualPayout - investment.investment_amount;
                const benefitBalance = annualPayout - investment.payout_claim;

                return {
                    ...investment,
                    annual_payout: annualPayout,
                    recovery_balance: recoveryBalance,
                    benefit_balance: benefitBalance
                };
            });
        },

        totalMyInvestment() {
            return this.investments.reduce((sum, inv) => sum + inv.investment_amount, 0);
        },

        totalMyPayoutClaim() {
            return this.investments.reduce((sum, inv) => sum + inv.payout_claim, 0);
        },

        totalAnnualPayout() {
            return this.enrichedInvestments.reduce((sum, inv) => sum + inv.annual_payout, 0);
        },

        myParticipationPercentage() {
            // Toto by sa malo počítať na základe celkových investícií všetkých investorov
            // Pre demo používame fixnú hodnotu
            const totalAllInvestments = 2687420 + this.totalMyInvestment;
            return ((this.totalMyInvestment / totalAllInvestments) * 100).toFixed(4);
        },

        keyMetrics() {
            return [
                {
                    label: 'Celková investícia',
                    value: this.formatCurrency(this.totalMyInvestment),
                    color: 'primary',
                    progress: (this.totalMyInvestment / 100000) * 100 // Ukážková progress
                },
                {
                    label: 'Celkový nárok',
                    value: this.formatCurrency(this.totalMyPayoutClaim),
                    color: 'success',
                    subtitle: 'S benefitom'
                },
                {
                    label: 'Ročná výplata',
                    value: this.formatCurrency(this.totalAnnualPayout),
                    color: 'info',
                    subtitle: 'Očakávaná'
                },
                {
                    label: 'ROI',
                    value: this.totalMyInvestment > 0 ?
                        ((this.totalAnnualPayout / this.totalMyInvestment) * 100).toFixed(1) + '%' : '0%',
                    color: 'warning',
                    subtitle: 'Ročne'
                }
            ];
        },

        investmentRecoveryProgress() {
            if (this.totalMyInvestment === 0) return 0;
            return Math.min(100, (this.totalAnnualPayout / this.totalMyInvestment) * 100);
        },

        benefitProgress() {
            if (this.totalMyPayoutClaim === 0) return 0;
            return Math.min(100, (this.totalAnnualPayout / this.totalMyPayoutClaim) * 100);
        },

        // Ďalšia investícia
        investmentRules() {
            return [
                v => !!v || 'Suma je povinná',
                v => v >= 10 || 'Minimálna investícia je 10€',
                v => v % 10 === 0 || 'Len násobky 10€',
                v => v <= 1000000 || 'Maximum 1,000,000€'
            ];
        },

        isValidAdditionalInvestment() {
            return this.additionalInvestment >= 10 &&
                this.additionalInvestment % 10 === 0 &&
                this.additionalInvestment <= 1000000;
        },

        getProjectedCoefficient() {
            // Simulácia výpočtu aktuálneho koeficientu
            const currentTotal = 2687420; // Z Excel analýzy
            const coefficientLevels = [
                { threshold: 1000000, coefficient: 5 },
                { threshold: 2000000, coefficient: 4 },
                { threshold: 3000000, coefficient: 3 },
                { threshold: 4000000, coefficient: 2 },
                { threshold: 10000000, coefficient: 1.5 }
            ];

            for (let level of coefficientLevels) {
                if (currentTotal < level.threshold) {
                    return level.coefficient;
                }
            }
            return 1.5;
        },

        additionalPayoutClaim() {
            return this.additionalInvestment * this.getProjectedCoefficient;
        },

        additionalROI() {
            if (this.additionalInvestment === 0) return 0;
            const projectedAnnualPayout = this.calculateProjectedPayout(this.additionalInvestment);
            return ((projectedAnnualPayout / this.additionalInvestment) * 100).toFixed(1);
        }
    },

    watch: {
        annualRevenue(newVal) {
            this.modeledAnnualRevenue = newVal;
        }
    },

    methods: {
        calculateAnnualPayout(investment) {
            // Výpočet ročnej výplaty na základe modelovaných tržieb a podielu
            const revenueForPayouts = this.modeledAnnualRevenue * (this.payoutPercentage / 100);
            const totalAllInvestments = 2687420; // Z Excel analýzy - celkové investície všetkých
            return revenueForPayouts * (investment.investment_amount / totalAllInvestments);
        },

        calculateProjectedPayout(amount) {
            const revenueForPayouts = this.modeledAnnualRevenue * (this.payoutPercentage / 100);
            const totalAllInvestments = 2687420 + amount;
            return revenueForPayouts * (amount / totalAllInvestments);
        },

        updateModeledRevenue() {
            // Trigger recalculation
            this.$forceUpdate();
        },

        toggleDashboardDetails() {
            this.showDashboardDetails = !this.showDashboardDetails;
        },

        getStatusColor(status) {
            const colors = {
                false: 'warning',
                true: 'success',
                'completed': 'info',
                'cancelled': 'error'
            };
            return colors[status] || 'grey';
        },

        getStatusIcon(status) {
            const icons = {
                false: 'mdi-clock',
                true: 'mdi-check-circle',
                'completed': 'mdi-trophy',
                'cancelled': 'mdi-close-circle'
            };
            return icons[status] || 'mdi-help-circle';
        },

        getStatusText(status) {
            const texts = {
                false: 'Čaká na úhradu',
                true: 'Aktívna',
                'completed': 'Dokončená',
                'cancelled': 'Zrušená'
            };
            return texts[status] || 'Neznámy';
        },

        getCoefficientColor(coefficient) {
            if (coefficient >= 5) return 'success';
            if (coefficient >= 4) return 'warning';
            if (coefficient >= 3) return 'orange';
            if (coefficient >= 2) return 'error';
            return 'grey';
        },

        formatDate(dateString) {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('sk-SK');
        },

        formatCurrency(amount) {
            // Handle null, undefined, empty string
            if (amount == null || amount === '') return '0€';

            // Convert string to number (handles Django DecimalField)
            const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

            // Check if conversion resulted in a valid number
            if (isNaN(numAmount)) return '0€';

            return new Intl.NumberFormat('sk-SK', {
                style: 'currency',
                currency: 'EUR'
            }).format(numAmount);
        },

        showContractDetail(investment) {
            // Emit event to parent to show contract detail
            this.$emit('show-contract', investment);
        },

        downloadContract(investment) {
            apiService.downloadItem(`/investment/${investment.id}/download-contract/`, `investicna_dohoda_${investment.id}.pdf`)
        },

        exportData() {
            // Export functionality
            const data = this.enrichedInvestments.map(inv => ({
                'Dátum': this.formatDate(inv.created_at),
                'Číslo zmluvy': inv.contract_number,
                'Investícia': inv.investment_amount,
                'Koeficient': inv.coefficient,
                'Nárok': inv.payout_claim,
                'Ročná výplata': inv.annual_payout,
                'Status': this.getStatusText(inv.status)
            }));

            // Simple CSV export
            const csv = this.convertToCSV(data);
            this.downloadCSV(csv, 'moje_investicie.csv');
        },

        convertToCSV(data) {
            if (!data.length) return '';

            const headers = Object.keys(data[0]);
            const csvContent = [
                headers.join(','),
                ...data.map(row => headers.map(header => `"${row[header]}"`).join(','))
            ].join('\n');

            return csvContent;
        },

        downloadCSV(csv, filename) {
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');

            if (link.download !== undefined) {
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', filename);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        },

        refreshData() {
            this.$emit('refresh');
        },

        investAdditional() {
            if (!this.isValidAdditionalInvestment) return;

            this.investingAdditional = true;

            // Simulate API call
            setTimeout(() => {
                this.$emit('invest-additional', this.additionalInvestment);
                this.investingAdditional = false;
                this.additionalInvestment = 1000; // Reset
            }, 1000);
        }
    }
};
</script>

<style scoped>
.personal-investments {
    margin-bottom: 24px;
}

.empty-state {
    min-height: 400px;
    display: flex;
    align-items: center;
}

.dashboard-card {
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
}

.metric-card {
    background: rgba(255, 255, 255, 0.8);
    border-radius: 8px;
    transition: all 0.2s ease;
}

.metric-card:hover {
    background: rgba(255, 255, 255, 0.95);
    transform: translateY(-2px);
}

.metric-value {
    font-size: 1.8rem !important;
}

.quick-invest-card {
    border: 2px dashed #4caf50;
    background-color: rgba(76, 175, 80, 0.05);
}

.v-data-table>>>.v-data-table__wrapper {
    border-radius: 8px;
}

@media (max-width: 600px) {
    .metric-value {
        font-size: 1.4rem !important;
    }

    .text-h5 {
        font-size: 1.3rem !important;
    }
}
</style>